import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchRestaurantPublic, submitFeedback } from '@/lib/data';

export default function FeedbackPage() {
  const { outletSlug } = useParams<{ outletSlug: string }>();
  const [params] = useSearchParams();
  const source = (params.get('src') === 'link' ? 'link' : 'qr') as 'qr' | 'link';

  const [restaurant, setRestaurant] = useState<{ id: string; name: string } | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!outletSlug) return;
    fetchRestaurantPublic(outletSlug)
      .then((r) => setRestaurant({ id: r.id, name: r.name }))
      .catch(() => setLoadErr('We could not find this restaurant.'));
  }, [outletSlug]);

  const submit = async () => {
    if (!restaurant) return;
    if (rating < 1) { setErr('Please tap a star to rate.'); return; }
    setBusy(true); setErr('');
    try {
      await submitFeedback({
        restaurantId: restaurant.id,
        rating,
        comment: comment.trim() || undefined,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        source,
      });
      setDone(true);
    } catch {
      setErr('Sorry, we could not submit your feedback. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center text-gray-600">
        {loadErr}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
        {done ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center text-green-600 text-3xl">
              <i className="ri-check-line"></i>
            </div>
            <h1 className="mt-4 text-xl font-bold text-gray-900">Thank you!</h1>
            <p className="mt-1 text-sm text-gray-500">Your feedback helps {restaurant?.name} get better.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-gray-900">How was your experience?</h1>
              {restaurant && <p className="text-sm text-gray-500 mt-1">at {restaurant.name}</p>}
            </div>

            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="text-4xl transition-transform hover:scale-110 cursor-pointer"
                  aria-label={`${n} star`}
                >
                  <i className={`${(hover || rating) >= n ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}></i>
                </button>
              ))}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Tell us more (optional)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 mb-3"
            />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)"
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500" />
            </div>

            {err && <p className="text-sm text-red-600 mb-3">{err}</p>}

            <button
              onClick={submit}
              disabled={busy}
              className="w-full py-3 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-50"
            >
              {busy ? 'Submitting…' : 'Submit Feedback'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
